import {
  ERROR_CATALOG,
  createValidationIssue,
  errorCodeSchema,
  errorResponseSchema,
  requestIdSchema,
  type ErrorCode,
  type ValidationIssue,
} from "@typedoc/contracts/errors";
import type { z } from "zod";

const CLIENT_ERRORS = {
  NETWORK_ERROR: "We couldn't connect. Check your connection and try again.",
  REQUEST_TIMEOUT: "The request timed out. Please try again.",
  REQUEST_CANCELLED: "The request was canceled.",
  INVALID_RESPONSE: "We couldn't read the service response. Please try again.",
  ACCESS_CHANGED: "Workspace access changed. Select your workspace again.",
  REGION_UNAVAILABLE:
    "Your workspace's regional service isn't available here. Contact your administrator.",
  AUTH_ACTION_FAILED: "We couldn't complete that request. Please try again.",
  UNKNOWN_ERROR: "Something went wrong. Please try again.",
  // Board N8's three failures. Unlike a sign-in refusal these say exactly what went wrong: the
  // address is already known to whoever is reading the code, so there is nothing left to
  // withhold, and a vague message here would just make a mistyped digit unrecoverable.
  INVALID_OTP: "That code isn't right. Check the most recent email and try again.",
  OTP_EXPIRED: "This code has expired. Send a new one to carry on.",
  TOO_MANY_ATTEMPTS: "Too many tries, so this code is closed. Send a fresh one to carry on.",
  EMAIL_NOT_VERIFIED: "Confirm your email address to finish signing in.",
} as const;

const AUTH_ACTION_MESSAGES = {
  "sign-in": "Sign-in failed. Check your email and password, then try again.",
  "sign-up": "The account couldn't be created. Check your details or try signing in.",
  "sign-out": "Sign-out couldn't be completed. Please try again.",
  session: "Your session couldn't be checked. Please try again.",
  "list-workspaces": "Your workspaces couldn't be loaded. Please try again.",
  "select-workspace": "The workspace couldn't be selected. Please try again.",
  token: "Workspace access couldn't be verified. Select your workspace or sign in again.",
  "verify-email": "We couldn't check that code. Please try again.",
  "resend-code": "We couldn't send a new code. Please try again.",
} as const;

export type AuthAction = keyof typeof AUTH_ACTION_MESSAGES;
export type AppErrorCode = ErrorCode | keyof typeof CLIENT_ERRORS;

type ErrorOptions = {
  status?: number | undefined;
  requestId?: string | undefined;
  action?: AuthAction | undefined;
  issues?: ValidationIssue[] | undefined;
};

/** Only catalogued messages may cross the UI boundary; raw server/SDK messages never do. */
export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly status: number | undefined;
  readonly requestId: string | undefined;
  readonly issues: readonly ValidationIssue[];

  constructor(code: AppErrorCode, options: ErrorOptions = {}) {
    const message =
      options.action === "sign-out" || (code === "AUTH_ACTION_FAILED" && options.action)
        ? AUTH_ACTION_MESSAGES[options.action]
        : code in CLIENT_ERRORS
          ? CLIENT_ERRORS[code as keyof typeof CLIENT_ERRORS]
          : ERROR_CATALOG[code as ErrorCode].message;
    super(message);
    this.name = "AppError";
    this.code = code;
    this.status = options.status;
    const requestId = requestIdSchema.safeParse(options.requestId);
    this.requestId = requestId.success ? requestId.data : undefined;
    this.issues =
      code === "VALIDATION_ERROR"
        ? (options.issues ?? []).map((issue) => createValidationIssue(issue.path, issue.code))
        : [];
  }
}

export function normalizeAppError(
  error: unknown,
  options: { signal?: AbortSignal; fallbackCode?: AppErrorCode } = {},
): AppError {
  if (options.signal?.aborted) {
    if (options.signal.reason instanceof AppError) return options.signal.reason;
    if (options.signal.reason instanceof Error && options.signal.reason.name === "TimeoutError") {
      return new AppError("REQUEST_TIMEOUT");
    }
    return new AppError("REQUEST_CANCELLED");
  }
  if (error instanceof AppError) return error;
  if (error instanceof Error && error.name === "AbortError")
    return new AppError("REQUEST_CANCELLED");
  if (error instanceof Error && error.name === "TimeoutError")
    return new AppError("REQUEST_TIMEOUT");
  return new AppError(options.fallbackCode ?? "UNKNOWN_ERROR");
}

function codeForStatus(status: number): ErrorCode {
  if (status === 400) return "BAD_REQUEST";
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 409) return "CONFLICT";
  if (status === 422) return "VALIDATION_ERROR";
  if (status === 429) return "RATE_LIMITED";
  if (status === 503) return "SERVICE_UNAVAILABLE";
  return "INTERNAL_ERROR";
}

type JsonResponse = Pick<Response, "ok" | "status" | "headers" | "json">;

export async function errorFromResponse(response: JsonResponse): Promise<AppError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Gateways can return HTML or no body. Use status-only copy, never that body.
  }
  return errorFromBody(body, response.status, response.headers.get("X-Request-Id") ?? undefined);
}

/** A failed answer's already-read body: the catalog envelope if it is one, else status-only copy. */
export function errorFromBody(body: unknown, status: number, requestId?: string): AppError {
  const parsed = errorResponseSchema.safeParse(body);
  if (parsed.success && ERROR_CATALOG[parsed.data.error.code].status === status) {
    return new AppError(parsed.data.error.code, {
      status,
      requestId: parsed.data.error.requestId,
      issues: parsed.data.error.issues,
    });
  }
  return new AppError(codeForStatus(status), { status, requestId });
}

/** Validate success at the boundary even though hc supplies compile-time route inference. */
export async function readApiResponse<T>(response: JsonResponse, schema: z.ZodType<T>): Promise<T> {
  if (!response.ok) throw await errorFromResponse(response);
  try {
    const parsed = schema.safeParse(await response.json());
    if (parsed.success) return parsed.data;
  } catch {
    // Parser details can contain customer data and belong outside the UI.
  }
  throw new AppError("INVALID_RESPONSE", {
    status: response.status,
    requestId: response.headers.get("X-Request-Id") ?? undefined,
  });
}

/** Better Auth retains its own flat wire response; this is the explicit SDK adapter. */
export function authError(error: unknown, action: AuthAction): AppError {
  if (error instanceof AppError) {
    return action === "sign-out"
      ? new AppError(error.code, { status: error.status, requestId: error.requestId, action })
      : error;
  }
  if (error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError")) {
    return normalizeAppError(error);
  }
  const fields = error && typeof error === "object" ? error : {};
  const status =
    "status" in fields && typeof fields.status === "number" ? fields.status : undefined;
  const requestId =
    "requestId" in fields && typeof fields.requestId === "string" ? fields.requestId : undefined;
  const code = errorCodeSchema.safeParse("code" in fields ? fields.code : undefined);
  const options = { status, requestId, action };
  if (error instanceof TypeError) return new AppError("NETWORK_ERROR", options);
  /**
   * Better Auth's own codes for the one-time code, which the shared catalogue does not carry.
   * They are read before the generic status mapping because all three arrive as a plain 400,
   * which would otherwise flatten "wrong digit" and "expired" into the same sentence and leave
   * the screen unable to offer the right way out of each.
   */
  const pluginCode = "code" in fields ? fields.code : undefined;
  if (
    pluginCode === "INVALID_OTP" ||
    pluginCode === "OTP_EXPIRED" ||
    pluginCode === "TOO_MANY_ATTEMPTS"
  ) {
    return new AppError(pluginCode, options);
  }
  /**
   * The one sign-in refusal that is named rather than generic. Naming it discloses nothing:
   * Better Auth only reaches it after the password has been checked, so anyone who can see this
   * already holds the credentials for the account it is about. Leaving it generic would strand
   * them — "check your email and password" is not something a correct password can act on.
   *
   * Sign-in only. That argument is about a password having already been checked, and enrollment
   * checks no password, so the same code arriving there would name an account without anyone
   * having proved anything about it. Nothing sends it today; the narrower branch is what keeps
   * that true if something ever does.
   */
  if (action === "sign-in" && pluginCode === "EMAIL_NOT_VERIFIED" && status === 403) {
    return new AppError("EMAIL_NOT_VERIFIED", options);
  }
  // Authentication/registration failures deliberately do not disclose whether an account exists.
  if (
    (action === "sign-in" || action === "sign-up") &&
    (status === undefined || status < 500) &&
    status !== 429
  ) {
    return new AppError("AUTH_ACTION_FAILED", options);
  }
  if (code.success && ERROR_CATALOG[code.data].status === status) {
    return new AppError(code.data, options);
  }
  if (status === 429) return new AppError("RATE_LIMITED", options);
  if (status !== undefined && status >= 500) return new AppError("AUTH_UNAVAILABLE", options);
  if (status === 401 && action !== "sign-out") return new AppError("UNAUTHORIZED", options);
  return new AppError("AUTH_ACTION_FAILED", options);
}
